<script setup lang="ts">
import AnyIcon from '@/core/elements/AnyIcon.vue';
import { hexToRgba } from '@/utils/color.utils';
import { EIcon } from 'common/enum/misc.enum';
import { computed, toRefs } from 'vue';

const props = withDefaults(
  defineProps<{
    theme?: 'primary' | 'text' | 'note' | 'secondary' | 'secondary-filled' | 'primary-filled' | 'default' | 'graph';
    iconName: EIcon | string;
    color?: string;
    type?: 'button' | 'submit';
    disabled?: boolean;
    size?: 'sm' | 'md' | 'lg' | 'button' | 'pdf-button';
    noOpacityOnDisabled?: boolean;
  }>(),
  {
    color: undefined,
    theme: 'primary',
    disabled: false,
    type: 'button',
    size: 'md',
    noOpacityOnDisabled: false,
  },
);
const { iconName, theme, disabled, color, type } = toRefs(props);

const iconStyle = computed(() => {
  if (!color.value) return {};

  if (theme.value === 'secondary') {
    return {
      color: 'white',
      backgroundColor: color.value,
    };
  }

  if (theme.value === 'graph') {
    return {
      color: 'white',
    };
  }

  if (theme.value === 'primary') {
    return {
      color: color.value,
      borderColor: color.value,
    };
  }

  return {
    color: color.value,
    backgroundColor: hexToRgba(color.value, 0.2),
  };
});
</script>

<template>
  <button
    :type="type"
    :disabled="disabled"
    :style="iconStyle"
    :class="[
      'flex items-center justify-center shrink-0',
      'transition duration-300',
      'outline-inner',
      'active:bg-tint active:text-white hover:bg-primary/20',
      'rounded-button',
      theme === 'secondary' ? 'text-secondary' : 'text-tint',
      theme === 'secondary-filled' && '!text-primary bg-surface-secondary',
      theme === 'primary-filled' && 'bg-primary text-white focus-visible:outline-offset-[2px]',
      theme === 'text' && '!text-primary',
      theme === 'note' && '!text-primary note-state-effects',
      theme === 'graph' && 'bg-white/20',
      theme === 'primary' && 'border-2 bg-surface-primary!',
      {
        'size-icon-button': size === 'md' || !size,
        'size-[22px]': size === 'sm',
        'size-[40px]': size === 'lg',
        'size-button': size === 'button',
        'size-[26px]': size === 'pdf-button',
      },
      disabled && !noOpacityOnDisabled && 'opacity-50 pointer-events-none cursor-default',
      disabled && noOpacityOnDisabled && 'pointer-events-none cursor-default',
    ]"
  >
    <AnyIcon
      :icon-name="iconName"
      :class="[
        'flex-shrink-0',
        {
          'size-icon-sm': size === 'md' || !size,
          'size-icon-md': size === 'button' || size === 'lg',
          'size-[15px]': size === 'sm',
          'size-[16px]': size === 'pdf-button',
        },
      ]"
    />
  </button>
</template>
